import React, { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import SoundManager from '../utils/SoundManager';
import { disposeScene } from '../utils/ThreeCleanup';

const AgrouVisualization: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [showIntro, setShowIntro] = useState(true);
  
  useEffect(() => {
    if (!containerRef.current) return;
    
    // After a brief intro, hide it and show the 3D visualization
    setTimeout(() => {
      setShowIntro(false);
      SoundManager.playSound('command');
    }, 2000);
    
    // Setup Three.js scene
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x000000);
    
    const camera = new THREE.PerspectiveCamera(
      75, 
      containerRef.current.clientWidth / containerRef.current.clientHeight, 
      0.1, 
      1000
    );
    camera.position.z = 5;
    
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(containerRef.current.clientWidth, containerRef.current.clientHeight);
    containerRef.current.appendChild(renderer.domElement);
    
    // Create a group for all objects
    const group = new THREE.Group();
    scene.add(group);
    
    // Add light
    const ambientLight = new THREE.AmbientLight(0x404040);
    scene.add(ambientLight);
    
    const pointLight = new THREE.PointLight(0x5fe979, 1.5, 100);
    pointLight.position.set(0, 0, 5);
    scene.add(pointLight);
    
    // Function to create text with different styles
    const createTextMesh = (text: string, position: [number, number, number], scale: number, color: number, rotationSpeed: number) => {
      const canvas = document.createElement('canvas');
      canvas.width = 512;
      canvas.height = 256;
      const context = canvas.getContext('2d');
      
      if (context) {
        context.fillStyle = 'rgba(0, 0, 0, 0)';
        context.fillRect(0, 0, canvas.width, canvas.height);
        
        // Random text style
        const fontSize = Math.floor(Math.random() * 40) + 30;
        const fontStyles = ['normal', 'italic', 'bold', 'bold italic'];
        const fontStyle = fontStyles[Math.floor(Math.random() * fontStyles.length)];
        
        context.font = `${fontStyle} ${fontSize}px C64, monospace`;
        context.textAlign = 'center';
        context.textBaseline = 'middle';
        
        // Add glow effect
        const glowColor = '#' + color.toString(16).padStart(6, '0');
        context.shadowColor = glowColor;
        context.shadowBlur = 15;
        context.fillStyle = glowColor;
        
        // Draw text
        context.fillText(text, canvas.width / 2, canvas.height / 2);
        
        // Create texture and sprite
        const texture = new THREE.Texture(canvas);
        texture.needsUpdate = true;
        
        const material = new THREE.SpriteMaterial({
          map: texture,
          transparent: true,
          blending: THREE.AdditiveBlending
        });
        
        const sprite = new THREE.Sprite(material);
        sprite.position.set(...position);
        sprite.scale.set(scale, scale / 2, 1);
        
        // Store rotation speed as user data
        sprite.userData.rotationSpeed = rotationSpeed;
        
        return sprite;
      }
      
      return null;
    };
    
    // Create multiple "agrou agrou grou" texts with different styles
    const texts: THREE.Object3D[] = [];
    const phrases = [
      "agrou agrou grou",
      "AGROU AGROU GROU",
      "agrou AGROU grou",
      "Agrou Agrou Grou",
      "A G R O U",
      "a.g.r.o.u",
      "~agrou~",
      ">>> AGROU <<<"
    ];
    
    const colors = [
      0x5fe979, // green
      0xff6b6b, // red
      0xffd166, // yellow
      0x06d6a0, // teal
      0x118ab2, // blue
      0x8a2be2, // purple
      0xff00ff, // magenta
      0x00ffff  // cyan
    ];
    
    // Create 15 random text sprites
    for (let i = 0; i < 15; i++) {
      const phrase = phrases[Math.floor(Math.random() * phrases.length)];
      const color = colors[Math.floor(Math.random() * colors.length)];
      
      // Random position
      const x = (Math.random() - 0.5) * 10;
      const y = (Math.random() - 0.5) * 8;
      const z = (Math.random() - 0.5) * 5;
      
      const scale = Math.random() * 3 + 2;
      const rotationSpeed = (Math.random() - 0.5) * 0.02;
      
      const textMesh = createTextMesh(phrase, [x, y, z], scale, color, rotationSpeed);
      if (textMesh) {
        group.add(textMesh);
        texts.push(textMesh);
      }
    }
    
    // Add particles for effect
    const particlesCount = 500;
    const particlesGeometry = new THREE.BufferGeometry();
    const particlePositions = new Float32Array(particlesCount * 3);
    
    for (let i = 0; i < particlesCount * 3; i += 3) {
      particlePositions[i] = (Math.random() - 0.5) * 20;
      particlePositions[i + 1] = (Math.random() - 0.5) * 20;
      particlePositions[i + 2] = (Math.random() - 0.5) * 20;
    }
    
    particlesGeometry.setAttribute('position', new THREE.BufferAttribute(particlePositions, 3));
    
    const particleMaterial = new THREE.PointsMaterial({
      color: 0x5fe979,
      size: 0.1,
      transparent: true,
      opacity: 0.7,
      blending: THREE.AdditiveBlending
    });
    
    const particles = new THREE.Points(particlesGeometry, particleMaterial);
    scene.add(particles);
    
    // Animation loop
    let time = 0;
    let animationFrameId = 0;
    const animate = () => {
      animationFrameId = requestAnimationFrame(animate);
      time += 0.01;
      
      // Rotate group slowly
      group.rotation.y = Math.sin(time * 0.2) * 0.5;
      group.rotation.x = Math.cos(time * 0.1) * 0.2;
      
      // Animate each text sprite
      texts.forEach(sprite => {
        const speed = sprite.userData.rotationSpeed;
        sprite.rotation.z += speed;
        
        // Pulsate size
        const pulse = Math.sin(time * 2 + sprite.position.x) * 0.1 + 1;
        sprite.scale.x = sprite.scale.x * 0.99 + pulse * sprite.scale.x * 0.01;
        sprite.scale.y = sprite.scale.y * 0.99 + pulse * sprite.scale.y * 0.01;
      });
      
      // Animate particles
      particles.rotation.y += 0.001;
      particles.rotation.x += 0.0005;
      
      // Animate point light
      pointLight.intensity = 1 + Math.sin(time * 2) * 0.5;
      const hue = (time * 0.1) % 1;
      pointLight.color.setHSL(hue, 0.7, 0.5);
      
      renderer.render(scene, camera);
    };
    
    animate();
    
    // Handle window resize
    const handleResize = () => {
      if (!containerRef.current) return;
      
      const width = containerRef.current.clientWidth;
      const height = containerRef.current.clientHeight;
      
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      
      renderer.setSize(width, height);
    };
    
    window.addEventListener('resize', handleResize);
    
    // Play sound effects occasionally
    let soundTimeoutId = 0;
    const playSoundEffect = () => {
      if (Math.random() > 0.7) {
        SoundManager.playSound('command');
      }

      soundTimeoutId = window.setTimeout(playSoundEffect, Math.random() * 3000 + 2000);
    };

    playSoundEffect();

    // Cleanup
    return () => {
      cancelAnimationFrame(animationFrameId);
      clearTimeout(soundTimeoutId);
      window.removeEventListener('resize', handleResize);

      if (containerRef.current && containerRef.current.contains(renderer.domElement)) {
        containerRef.current.removeChild(renderer.domElement);
      }
      disposeScene(scene, renderer);
    };
  }, []);
  
  return (
    <div className="w-full h-full flex flex-col items-center justify-center">
      {showIntro && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-black bg-opacity-90 text-[var(--terminal-text)]">
          <div className="text-4xl font-bold animate-pulse">AGROU AGROU GROU</div>
        </div>
      )}
      <div className="relative w-full h-full" ref={containerRef}></div>
    </div>
  );
};

export default AgrouVisualization;